import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiBookOpen, FiDownload, FiTrash2, FiList, FiEdit2, FiCheck, FiX } = FiIcons;

export default function Header({
  onDownload, onClear, partCount,
  bookTitle, bookAuthor, onTitleChange, onAuthorChange,
  onToggleToc, showToc, hasHeadings
}) {
  const [editingMeta, setEditingMeta] = useState(false);
  const [tmpTitle, setTmpTitle] = useState('');
  const [tmpAuthor, setTmpAuthor] = useState('');

  const openEdit = () => {
    setTmpTitle(bookTitle);
    setTmpAuthor(bookAuthor);
    setEditingMeta(true);
  };

  const saveEdit = () => {
    onTitleChange(tmpTitle);
    onAuthorChange(tmpAuthor);
    setEditingMeta(false);
  };

  return (
    <header className="bg-slate-900 border-b border-slate-800 text-white py-3 px-5 sticky top-0 z-20">
      <div className="max-w-[1700px] mx-auto flex flex-wrap items-center justify-between gap-3">
        {/* Brand */}
        <div className="flex items-center space-x-3 shrink-0">
          <div className="bg-indigo-500 p-2 rounded-lg shadow-lg shadow-indigo-500/20">
            <SafeIcon icon={FiBookOpen} className="text-lg" />
          </div>
          <div>
            <h1 className="text-base font-black tracking-tight leading-none">MD Book Builder</h1>
            <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-widest">Ebook Compiler</p>
          </div>
        </div>

        {/* Book meta */}
        <div className="flex-1 min-w-0 max-w-sm">
          {editingMeta ? (
            <div className="flex items-center gap-2">
              <input
                value={tmpTitle}
                onChange={e => setTmpTitle(e.target.value)}
                placeholder="Book Title"
                className="flex-1 min-w-0 bg-slate-800 border border-slate-600 text-white text-sm rounded-lg px-3 py-1.5 outline-none focus:border-indigo-500"
              />
              <input
                value={tmpAuthor}
                onChange={e => setTmpAuthor(e.target.value)}
                placeholder="Author"
                className="w-28 bg-slate-800 border border-slate-600 text-white text-sm rounded-lg px-3 py-1.5 outline-none focus:border-indigo-500"
              />
              <button onClick={saveEdit} className="p-1.5 bg-green-600 hover:bg-green-500 rounded-lg transition-colors">
                <SafeIcon icon={FiCheck} className="text-sm" />
              </button>
              <button onClick={() => setEditingMeta(false)} className="p-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors">
                <SafeIcon icon={FiX} className="text-sm" />
              </button>
            </div>
          ) : (
            <button
              onClick={openEdit}
              className="group flex items-center gap-2 text-left max-w-full overflow-hidden hover:bg-slate-800 px-3 py-1.5 rounded-lg transition-colors"
            >
              <div className="min-w-0 overflow-hidden">
                <p className="text-sm font-bold text-white truncate">
                  {bookTitle || <span className="text-slate-500 font-normal italic">Untitled Book</span>}
                </p>
                {bookAuthor && <p className="text-xs text-slate-400 truncate">by {bookAuthor}</p>}
              </div>
              <SafeIcon icon={FiEdit2} className="text-slate-500 group-hover:text-indigo-400 text-xs shrink-0 transition-colors" />
            </button>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-2 shrink-0">
          <div className="hidden sm:flex items-center bg-slate-800 px-3 py-1.5 rounded-full border border-slate-700">
            <span className="w-2 h-2 rounded-full bg-green-500 mr-2 animate-pulse"></span>
            <span className="text-xs font-semibold text-slate-300">
              {partCount} {partCount === 1 ? 'Part' : 'Parts'}
            </span>
          </div>

          {hasHeadings && (
            <button
              onClick={onToggleToc}
              className={`flex items-center space-x-1.5 px-3 py-2 text-xs font-bold rounded-lg transition-all ${
                showToc ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
              title="Toggle Table of Contents"
            >
              <SafeIcon icon={FiList} />
              <span className="hidden md:inline">TOC</span>
            </button>
          )}

          <button
            onClick={onClear}
            disabled={partCount === 0}
            className="flex items-center space-x-1.5 px-3 py-2 text-sm font-medium text-slate-400 hover:text-red-400 hover:bg-slate-800/50 rounded-lg transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <SafeIcon icon={FiTrash2} />
            <span className="hidden sm:inline text-xs">Clear</span>
          </button>

          <button
            onClick={onDownload}
            disabled={partCount === 0}
            className="flex items-center space-x-2 px-5 py-2 text-xs font-black bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg transition-all shadow-lg shadow-indigo-600/30 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none uppercase tracking-wide"
          >
            <SafeIcon icon={FiDownload} />
            <span>Download .MD</span>
          </button>
        </div>
      </div>
    </header>
  );
}